import { Badge, Box, Button, DrawerBody, DrawerFooter, Flex, FormControl, FormHelperText, FormLabel, Input, List, ListIcon, ListItem, Progress, Select, Spinner, Stack, Text, useToast } from "@chakra-ui/react";
import { DrawerContext } from "components/drawer";
import { AuthContext } from "contexts/AuthContext";
import { useUpload } from "hooks/useContent";
import { useLocation } from "hooks/useLocation";
import { useStorage } from "hooks/useStorage";
import { create, save } from "lib/firebase";
import { getImageSize, resizeImageFile } from "lib/image";
import { sanitize, uniqueFilename } from "lib/utils";
import { formatBytes, formatSeconds } from "lib/utils";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { MdAvTimer } from "react-icons/md";

// Images are recognised by MIME type, with an extension fallback for browsers
// that leave File.type empty.
const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|avif)$/i
const isImageFile = (file) => (file.type || '').startsWith('image/') || IMAGE_EXTENSIONS.test(file.name || '')

export default function ContentUploadModal({ onFinish }) {
  const { user } = useContext(AuthContext)
  const toast = useToast()

  const { closeDrawer } = useContext(DrawerContext)

  const { upload } = useUpload({user})

  const [fileDir, setFileDir] = useState({})
  const [progress, setProgress] = useState({})

  const progressListener = (val, name) => {
    setProgress((progress) => ({...progress, [name]: val}))
  }

  const isUploading = () => {
    return Object.values(progress).filter(p => (p > 0 && p < 100)).length > 0
  }

  const uploadFiles = async () => {
    // check if all files have duration
    const noDuration = Object.values(fileDir).find(file => !file.duration || file.duration < 1)
    if (noDuration) {
      toast({ title: 'Error!', description: 'Files need to have duration set.', status: 'error', duration: null, isClosable: true })
      return
    }

    await Promise.all(Object.values(fileDir).map(async (file, i, arr) => {

      const displayName = sanitize(file.name)
      // The moment of the upload (not the date of the file, which can be old), as an ISO-8601 UTC string
      const uploadTime = moment().toISOString()
      const record = {
        filename: displayName, displayName: displayName, size: file.size, clientId: user.clientId, duration: file.duration, type: file.type, timestamp: uploadTime
      }
      // The picture size, when the browser could read it (the database rejects an undefined field).
      // A video gets its final size from the Cloud Function after transcoding.
      if (file.width > 0 && file.height > 0) {
        record.width = file.width
        record.height = file.height
      }
      const content = await create('content', record)

      // Make the stored filename unique so consumers that key off basename
      // (e.g. Android playlists) never collide; the UI shows displayName.
      content.filename = uniqueFilename(file.name, content.id)

      const { url } = await upload(content, file.file, progressListener, i, arr.length)

      content.url = url
      content.timestamp = uploadTime
      await save('content', content.id, content)
    }))
    closeDrawer()
    onFinish && onFinish()
    toast({ title: 'Content uploaded!', status: 'success', duration: null, isClosable: true })
  }

  const prepare = async (files) => {
    const fileDirs = await files.reduce(async (sp, f, i) => {
      const s = await sp
      const file = {}
      file.file = f
      file.name = sanitize(f.name)
      file.size = f.size
      file.type = isImageFile(f) ? 'image': 'video'
      
      // file.duration = await getVideoDuration(f)
      if (file.type === 'video') {
        try {
          const video = await loadVideo(f)
          file.duration = video.duration
          // Size of the video as the browser reports it; the Cloud Function replaces it with the
          // size of the transcoded file
          file.width = video.videoWidth
          file.height = video.videoHeight
        } catch (e) {
          // Skip files that can't be read instead of breaking the whole selection
          toast({ title: 'Error!', description: `${f.name}: ${e.message || e}`, status: 'error', duration: null, isClosable: true })
          return s
        }
      } else {
        file.duration = 0
        // Downscale oversized images (e.g. >4K) before upload so they don't
        // break decoding/playback on the device. Falls back to the original
        // file if resizing isn't possible.
        try {
          const resized = await resizeImageFile(f)
          file.file = resized
          file.size = resized.size
        } catch (e) {
          console.warn('Image resize failed, uploading original', e)
        }
        // The size of the file that is really uploaded (after the resize)
        const size = await getImageSize(file.file)
        if (size) {
          file.width = size.width
          file.height = size.height
        }
      }

      file.progress = 0

      console.log('File prepared', file)
      s[file.name] = file
      return s
    }, {})

    setFileDir(fileDirs)
  }


  const loadVideo = file => new Promise((resolve, reject) => {
    try {
        let video = document.createElement('video')
        video.preload = 'metadata'

        video.onloadedmetadata = function () {
            resolve(this)
        }

        video.onerror = function () {
            reject("Invalid video. Please select a video file.")
        }

        video.src = window.URL.createObjectURL(file)
    } catch (e) {
        reject(e)
    }
  })

  return (
    <>
      <DrawerBody>
        <Stack direction='column' spacing={4}>
          <FormControl>
            <FormLabel>Selected files</FormLabel>
            <Input type='file' py={4} height={16} multiple onChange={e => prepare(Array.from(e.target.files))} accept="image/*, video/*" />
          </FormControl>
          <List spacing={6}>
            <Stack direction='row' alignItems='center' gap='4' wrap='wrap'>
              <Text fontSize='sm' w='100%'>Filename</Text>
              <Text fontSize='sm' w='32'>Duration (s)</Text>
              <Text fontSize='sm' w='16'>Size</Text>
              <Text fontSize='sm' w='24'>Resolution</Text>
            </Stack>
            {Object.values(fileDir).map((file, index) => (
              <ListItem key={file.name}>
                <Stack>
                  <Stack direction='row' alignItems='center' gap='4' wrap='wrap'>
                    <Text fontSize='sm' w='100%' fontWeight='medium'>{sanitize(file.name)}</Text>
                    {file.type.includes('image')
                      ? <Input w='32' size='sm' type='number' placeholder="Set duration" onChange={e => setFileDir((fileDir) => ({...fileDir, [file.name]: {...file, duration: parseInt(e.target.value)}}))} />
                      : <Text fontSize='sm' w='32' >{formatSeconds(file.duration)}</Text>
                    }
                    <Text fontSize='sm' w='16' >{formatBytes(file.size)}</Text>
                    <Text fontSize='sm' w='24' >{file.width > 0 && file.height > 0 ? `${file.width}x${file.height}` : ''}</Text>
                  </Stack>
                  <Flex direction='row' alignItems='center'>
                    <Progress width='full' colorScheme='green' size='sm' value={progress[sanitize(file.name)] || 0} />
                  </Flex>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <Button colorScheme='brand' borderRadius="30px" w={120} me="10px" onClick={() => uploadFiles()} disabled={isUploading()}>
          {isUploading() ? <Spinner /> : 'Upload'}
        </Button>
      </DrawerFooter>
    </>
  );
}